import { safeParseJson } from '../utilities/jsonUtil';

/**
 * FIELDS_CONFIG — single source of truth for every eligibility rule on the
 * Eligibility Engine page. Each entry declares the rule's contract: `id`,
 * `label`, `valueType` (picks the editor in ValueSelector) plus value-type
 * specific props (`options`, `numberValue`, `rangeMin/Max`, `staticText`, ...).
 *
 * mapToComponentFromStore / mapToStoreFromComponent / mapToApiFromStore —
 * per-rule adapters that move a rule's value across three shapes:
 *   • stored value    — what the Redux store holds while editing (display-only:
 *                       just the fields the component needs to render)
 *   • component state — the normalized contract editors consume
 *                       (`numberValue`, `value`, `options`)
 *   • API parameters  — the shape persisted to the backend on save, which may
 *                       carry fields the UI never shows (e.g. `code`)
 *
 *   - mapToComponentFromStore(value, rule)    → stored JSON → component state (on render);
 *                       `rule` is passed for catalogs served outside `value`
 *                       (e.g. `allowedChannels`)
 *   - mapToStoreFromComponent(newValue, rule) → component state → stored JSON (on change);
 *                       stores ONLY display fields, so it never needs API-only data
 *   - mapToApiFromStore(rule)                 → stored JSON → API JSON (on save only);
 *                       takes the whole rule, because the API shape may need
 *                       more than `value`: `catalog` (the original API value
 *                       captured at load, carrying API-only fields like `code`)
 *                       or a catalog served outside the value (`allowedChannels`)
 *   e.g. value => ({ numberValue: safeParseJson(value, {}).max })
 *        numberValue => JSON.stringify({ max: numberValue })
 *
 * Most rules need no mapToApiFromStore: their stored shape already equals the API
 * shape. Declare it only when the API carries fields absent from the component
 * state (see `autoCheckActivityCodeEligibilityRule`, `fuelTypeEligibilityRule`).
 *
 * A `multiselect` rule that omits the map* adapters falls back to the standard
 * `{ types: [{ name, enabled }] }` contract handled by ValueSelector's default
 * adapters.
 */
export const FIELDS_CONFIG = [
  {
    id: 'ageEligibilityRule',
    valueType: 'number_input',
    mapToComponentFromStore: value => ({ numberValue: safeParseJson(value, {}).max }),
    mapToStoreFromComponent: numberValue => JSON.stringify({ max: numberValue }),
  },
  {
    id: 'yearEligibilityRule',
    valueType: 'number_input',
    mapToComponentFromStore: value => ({ numberValue: safeParseJson(value, {}).min }),
    mapToStoreFromComponent: numberValue => JSON.stringify({ min: numberValue }),
  },
  {
    id: 'lightColorEligibilityRule',
    valueType: 'multiselect',
  },
  {
    id: 'announcementEligibilityRule',
    valueType: 'multiselect',
  },
  {
    id: 'conditionReportEligibilityRule',
    valueType: 'number_input',
    mapToComponentFromStore: value => ({ numberValue: safeParseJson(value, {}).min }),
    mapToStoreFromComponent: numberValue => JSON.stringify({ min: numberValue }),
  },
  {
    id: 'crTypeEligibilityRule',
    valueType: 'static_text',
    staticText: 'include only Manheim-classified CRs',
  },
  {
    id: 'crossoverEligibilityRule',
    valueType: 'static_text',
    staticText: 'do not allow crossover',
  },
  {
    id: 'fuelTypeEligibilityRule',
    valueType: 'multiselect',
    mapToComponentFromStore: value => {
      const fuelTypes = safeParseJson(value, {}).fuelTypes || [];
      return {
        options: fuelTypes.map(ft => ft.name),
        value: JSON.stringify(
          fuelTypes.filter(ft => ft.enabled).map(ft => ft.name)
        ),
      };
    },
    mapToStoreFromComponent: (newValue, rule) => {
      const fuelTypes = safeParseJson(rule.value, {}).fuelTypes || [];
      const selected = safeParseJson(newValue, []);
      return JSON.stringify({
        fuelTypes: fuelTypes.map(({ name }) => ({
          name,
          enabled: selected.includes(name),
        })),
      });
    },
    mapToApiFromStore: ({ value, catalog }) => {
      const catalogByName = new Map(
        (safeParseJson(catalog, {}).fuelTypes || []).map(ft => [ft.name, ft])
      );
      const fuelTypes = safeParseJson(value, {}).fuelTypes || [];
      return JSON.stringify({
        fuelTypes: fuelTypes.map(({ name, enabled }) => ({
          ...(catalogByName.get(name) || {}),
          name,
          enabled,
        })),
      });
    },
  },
  {
    id: 'ineligibleLaneEligibilityRule',
    valueType: 'static_text',
    staticText: 'automatically fetched from ineligible_lanes table',
  },
  {
    id: 'mmrEligibilityRule',
    valueType: 'number_input',
    mapToComponentFromStore: value => ({ numberValue: safeParseJson(value, {}).max }),
    mapToStoreFromComponent: numberValue => JSON.stringify({ max: numberValue }),
  },
  {
    id: 'odometerEligibilityRule',
    valueType: 'number_input',
    mapToComponentFromStore: value => ({ numberValue: safeParseJson(value, {}).max }),
    mapToStoreFromComponent: numberValue => JSON.stringify({ max: numberValue }),
  },
  {
    id: 'oveManExEligibilityRule',
    valueType: 'multiselect',
  },
  {
    id: 'pricingDollarOverMmrEligibilityRule',
    valueType: 'number_input',
    mapToComponentFromStore: value => ({ numberValue: safeParseJson(value, {}).maxDelta }),
    mapToStoreFromComponent: numberValue => JSON.stringify({ maxDelta: numberValue }),
  },
  {
    id: 'pricingPercentOfMmrEligibilityRule',
    valueType: 'range',
    mapToComponentFromStore: value => {
      const { minPct: min, maxPct: max } = safeParseJson(value, {});
      return { value: JSON.stringify({ min, max }) };
    },
    mapToStoreFromComponent: value => {
      const { min: minPct, max: maxPct } = safeParseJson(value, {});
      return JSON.stringify({ minPct, maxPct });
    },
  },
  { // US1923674 / US2059288: [FE] Sales Channel Front-End Configuration
    // The option pool arrives out-of-band on `rule.allowedChannels`
    // (GetRulesByProductCode only), so the adapters read the rule, not just the
    // value; partnerName/channelName map onto the nested multiselect's
    // domain-agnostic groupName/name contract.
    id: 'salesChannelEligibilityRule',
    valueType: 'nested_multiselect',
    mapToComponentFromStore: (value, rule) => {
      // A saved `types` wins; it is absent only right after the rule is added,
      // and then the pool's own `enabled` flag seeds the selection. An id
      // missing from a saved `types` reads as false, so a channel added to the
      // pool later never opts itself into an existing configuration.
      const stored = safeParseJson(value, {}).types;
      const configured = Array.isArray(stored)
        ? new Map(stored.map(type => [type.id, type.enabled !== false]))
        : null;

      const items = (rule.allowedChannels || []).map(channel => ({
        id: channel.id,
        groupName: channel.partnerName,
        name: channel.channelName,
        checked: configured
          ? configured.get(channel.id) === true
          : channel.enabled === true,
      }));

      // The pool has no contractual order (no ORDER BY on the APMS side).
      items.sort(
        (a, b) => String(a.groupName).localeCompare(String(b.groupName), undefined, { sensitivity: 'base' })
          || String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
      );

      return { value: JSON.stringify(items) };
    },
    // Persist the WHOLE pool: unselected channels need `enabled: false`,
    // otherwise the backend treats them as allowed.
    mapToStoreFromComponent: newValue => JSON.stringify({
      types: safeParseJson(newValue, []).map(({ id, checked }) => ({
        id,
        enabled: checked === true,
      })),
    }),
    // Rebuild `types` from the live pool on save, so the published payload is
    // always the whole pool and nothing else: ids no longer in the pool are
    // dropped, and a legacy `{ allowedChannelIds: [...] }` value never reaches
    // the API (this runs for every rule on publish, edited or not).
    mapToApiFromStore: rule => {
      const { value, allowedChannels: pool } = rule;
      // Pool unknown (`null`) is NOT an empty pool: filtering against it would
      // publish `{ types: [] }`, which the backend reads as "all channels
      // allowed". Leave the stored value untouched instead.
      if (!Array.isArray(pool)) return value;

      // Ids round-trip through a JSON scalar — compare them as strings so a
      // number/string mismatch cannot silently drop the whole selection.
      const stored = safeParseJson(value, {}).types;
      const configured = Array.isArray(stored)
        ? new Map(stored.map(type => [String(type.id), type.enabled !== false]))
        : null;

      return JSON.stringify({
        types: pool.map(channel => ({
          id: channel.id,
          // Mirrors mapToComponentFromStore: no saved `types` (legacy value or
          // a freshly added rule) falls back to the pool's own `enabled` flag,
          // so what gets published matches what the page shows.
          enabled: configured
            ? configured.get(String(channel.id)) === true
            : channel.enabled === true,
        })),
      });
    },
  },
  {
    id: 'purchasePriceEligibilityRule',
    valueType: 'range',
  },
  {
    id: 'traStatusEligibilityRule',
    valueType: 'static_text',
    staticText: 'do not allow TRA',
  },
  {
    id: 'vinBlacklistEligibilityRule',
    valueType: 'link',
    valueLinkText: 'automatically fetched from the VIN Blacklist, to edit go',
    valueLinkUrl: '/vin_blacklists/',
  },
  {
    id: 'vehicleTypeEligibilityRule',
    valueType: 'static_text',
    staticText: 'automatically fetched from ineligible_vehicle_types table',
  },
  {
    id: 'titleEligibilityRule',
    valueType: 'static_text',
    staticText: '',
  },
  {
    id: 'businessUnitEligibilityRule',
    valueType: 'multiselect',
  },
  {
    id: 'previousTRAEligibilityRule',
    valueType: 'static_text',
    staticText: 'deny vehicle if previous TRA problem file is found',
  },
  {
    id: 'autoCheckActivityCodeEligibilityRule',
    valueType: 'multiselect',
    mapToComponentFromStore: value => {
      const types = safeParseJson(value, {}).types || [];
      return {
        options: types.map(type => type.name),
        value: JSON.stringify(
          types.filter(type => type.enabled).map(type => type.name)
        ),
      };
    },
    mapToStoreFromComponent: (newValue, rule) => {
      const types = safeParseJson(rule.value, {}).types || [];
      const selected = safeParseJson(newValue, []);
      return JSON.stringify({
        types: types.map(({ name }) => ({
          name,
          enabled: selected.includes(name),
        })),
      });
    },
    mapToApiFromStore: ({ value, catalog }) => {
      const codeByName = new Map(
        (safeParseJson(catalog, {}).types || []).map(type => [type.name, type.code])
      );
      const types = safeParseJson(value, {}).types || [];
      return JSON.stringify({
        types: types.map(({ name, enabled }) => ({
          code: codeByName.get(name),
          name,
          enabled,
        })),
      });
    },
  },
];

// Maximum number of conditions that can be added per field
// Set to Infinity for unlimited conditions, or a number (e.g., 5) to limit
export const MAX_CONDITIONS_PER_FIELD = 5;

// Valid canonical values for the actionOnNull rule field
export const VALID_ACTION_ON_NULL_VALUES = ['IGNORE', 'DENY'];

// Default used when a stored/legacy actionOnNull value is missing or unrecognized
export const DEFAULT_ACTION_ON_NULL = VALID_ACTION_ON_NULL_VALUES[0];
